import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { type NotificationDto, isNotificationStaleRunning } from '@protoss/shared';
import { useNotificationsContext } from '../contexts/NotificationsContext';

/** Compact relative time ("just now", "5m", "3h", "2d"). */
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const s = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

/**
 * Header notification bell + unread badge + dropdown panel. Polls via useNotifications; each item is
 * notification-only unless it carries a CTA (ctaLabel + ctaHref) - then it renders an "Open"-style
 * button that deep-links and marks read. Tokens-only styling lives in home-page.css.
 */
export function NotificationBell(): React.ReactElement | null {
  const { enabled, notifications, unreadCount, markRead, markAllRead } = useNotificationsContext();
  const [open, setOpen] = React.useState(false);
  // Any background job in flight - drives the persistent spinner on the icon (survives the toaster).
  // A notification stuck 'running' past the stale window is treated as interrupted, so it no longer
  // keeps the spinner pulsing forever after its job died (e.g. a server restart killed the job).
  const hasRunning = notifications.some((n) => n.status === 'running' && !isNotificationStaleRunning(n));
  const ariaParts = [
    unreadCount > 0 ? `${unreadCount} unread` : '',
    hasRunning ? 'jobs running' : '',
  ].filter(Boolean);
  const bellLabel = ariaParts.length ? `Notifications (${ariaParts.join(', ')})` : 'Notifications';
  const rootRef = React.useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Close on outside click / Escape.
  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!enabled) return null;

  const onItem = (n: NotificationDto): void => {
    if (!n.readAt) markRead(n.uuid);
    if (n.ctaHref) {
      setOpen(false);
      if (/^https?:\/\//i.test(n.ctaHref)) window.open(n.ctaHref, '_blank', 'noopener,noreferrer');
      else navigate(n.ctaHref);
    }
  };

  return (
    <div className="notif-bell" ref={rootRef}>
      <button
        type="button"
        className="notif-bell__button"
        aria-label={bellLabel}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {unreadCount > 0 && (
          <span className="notif-bell__badge" aria-hidden="true">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
        {hasRunning && <span className="notif-bell__spinner" aria-hidden="true" />}
      </button>

      {open && (
        <div className="notif-bell__panel" role="dialog" aria-label="Notifications">
          <div className="notif-bell__panel-head">
            <span className="notif-bell__panel-title">Notifications</span>
            {unreadCount > 0 && (
              <button type="button" className="notif-bell__markall" onClick={() => markAllRead()}>
                Mark all read
              </button>
            )}
          </div>
          <div className="notif-bell__list">
            {notifications.length === 0 ? (
              <div className="notif-bell__empty">You&apos;re all caught up.</div>
            ) : (
              notifications.map((n) => {
                // A 'running' notification stuck past the stale window = its job died without
                // finalizing: render it as interrupted (muted dot, no pulse) instead of a perpetual
                // "In progress". Clicking still marks it read so the user can clear it.
                const interrupted = isNotificationStaleRunning(n);
                const dotKind = n.status === 'running' ? (interrupted ? 'interrupted' : 'running') : n.severity;
                const meta =
                  n.status === 'running'
                    ? interrupted
                      ? `Interrupted · ${relativeTime(n.createdAt)}`
                      : 'In progress'
                    : relativeTime(n.createdAt);
                return (
                  <button
                    key={n.uuid}
                    type="button"
                    className={`notif-bell__item${n.readAt ? '' : ' notif-bell__item--unread'}`}
                    onClick={() => onItem(n)}
                  >
                    <span className={`notif-bell__dot notif-bell__dot--${dotKind}`} aria-hidden="true" />
                    <span className="notif-bell__item-body">
                      <span className="notif-bell__item-title">{n.title}</span>
                      {n.body && <span className="notif-bell__item-text">{n.body}</span>}
                      <span className="notif-bell__item-meta">
                        {meta}
                        {n.ctaLabel && n.ctaHref && (
                          <span className="notif-bell__cta">{n.ctaLabel}</span>
                        )}
                      </span>
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
